import { ActionHandler } from "..";
import { setProductSwitchData } from "src/utils/redux/slices/productSwitch";
import {
  setCheckoutSaleItem,
  setCheckoutOrderOperation,
  SaleItemInfo,
} from "src/utils/redux/slices/checkoutOrders";
import { setRecommendationContext } from "src/utils/redux/slices/recommendationContext";
import getStore from "src/utils/redux/store";
import { getMedSKUByProductName } from "src/utils/plans";
import { getSelectedMedPlanType } from "src/utils/userSegment/features";
import { convertGqlSwitchOperationToProto } from "src/utils/orderConversions";
import { captureException } from "@utils/error";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import {
  loadAndStoreProductSwitchData,
  loadPreviewOrders,
} from "./loadProductSwitchData";

/**
 * Loads product switch data, then auto-selects the offer matching
 * the user's selectedMedPlanType from session storage.
 * Used when routing med users directly to product-switch checkout.
 */
export const loadAndSelectProductSwitchOffer: ActionHandler = async () => {
  await loadAndStoreProductSwitchData();

  const store = getStore();
  const { offers } = store.getState().productSwitch;
  const selectedSku = getSelectedMedPlanType();

  const matchingOffer = offers.find(
    (offer) => getMedSKUByProductName(offer.productName) === selectedSku
  );

  if (!matchingOffer?.switchOperation) {
    trackBuyflowEvent("BuyflowActionFailed", {
      errorMessage: `No matching offer for MED_SKU: ${selectedSku}`,
    });
    return;
  }

  try {
    store.dispatch(setProductSwitchData({ selectedOffer: matchingOffer }));

    const medSku = getMedSKUByProductName(matchingOffer.productName);
    const saleItemInfo: SaleItemInfo = {
      saleItemId: matchingOffer.switchOperation.saleItemId,
      trialSaleItemId: matchingOffer.switchOperation.trialSaleItemId,
      medSku,
    };
    store.dispatch(setCheckoutSaleItem(saleItemInfo));

    if (medSku) {
      store.dispatch(setRecommendationContext({ selectedMedPlan: medSku }));
    }

    const switchOperation = convertGqlSwitchOperationToProto(
      matchingOffer.switchOperation
    );
    store.dispatch(setCheckoutOrderOperation({ switchOperation }));

    store.dispatch(
      setProductSwitchData({
        isDeferredSwitch:
          // eslint-disable-next-line no-underscore-dangle
          matchingOffer.switchOperation?.switchType?.__typename ===
          "NextBillingCycleSwitch",
      })
    );

    await loadPreviewOrders();
  } catch (error) {
    captureException(error, "load-and-select-product-switch-offer-failed");
    trackBuyflowEvent("BuyflowActionFailed", {
      errorMessage: `Failed to select product switch offer: ${error}`,
    });
  }
};
